import { styled } from "@mui/material/styles";
import React, { useLayoutEffect, useRef, useState } from "react";
import { focusRing, reducedMotion } from "./tokens";
const GAP = 2;
const Track = styled("div")({
    position: "relative",
    display: "flex",
    height: "var(--j-btn-h)",
    padding: GAP,
    gap: GAP,
    borderRadius: "var(--j-r-sm)",
    background: "var(--j-well)",
    minWidth: 0,
    "&[data-fit=true] > button": {
        flex: "1 1 auto"
    },
    "&[role=tablist]": {
        alignSelf: "stretch",
        height: "auto",
        padding: 0,
        background: "none",
        borderRadius: 0,
        "& > [aria-hidden]": {
            display: "none"
        },
        "& > button": {
            flex: "0 1 auto",
            padding: "0 var(--j-sp-2)",
            borderRadius: 0
        },
        "& > button:hover": {
            color: "var(--j-ink)"
        },
        "& > button[aria-selected=true]": {
            boxShadow: "inset 0 -2px 0 var(--j-accent)"
        }
    }
});
const Thumb = styled("span")({
    position: "absolute",
    top: GAP,
    bottom: GAP,
    left: GAP,
    borderRadius: "var(--j-r-xs)",
    background: "var(--j-surface)",
    outline: "1px solid var(--j-line)",
    transition: "transform var(--j-dur-size) var(--j-ease)",
    [reducedMotion]: {
        transition: "none"
    }
});
const Segment = styled("button")({
    ...focusRing,
    position: "relative",
    flex: 1,
    minWidth: 0,
    padding: "0 var(--j-sp-2)",
    border: 0,
    borderRadius: "var(--j-r-xs)",
    background: "transparent",
    fontSize: "var(--j-fs-md)",
    fontWeight: 500,
    color: "var(--j-ink-2)",
    cursor: "pointer",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    transition: "color 150ms",
    "@media (max-width: 340px)": {
        padding: "0 var(--j-sp-1)"
    },
    "&[aria-selected=true], &[aria-checked=true]": {
        color: "var(--j-ink)"
    }
});
export function SegmentedControl({ options, value, onChange, role = "tablist", "aria-label": ariaLabel, getPanelId, getTabId, fitContent = false, className }) {
    const buttonsRef = useRef([]);
    const [measured, setMeasured] = useState(null);
    const selectedIndex = Math.max(0, options.findIndex((option)=>option.value === value));
    const count = options.length;
    const isTabs = role === "tablist";
    useLayoutEffect(()=>{
        const selected = buttonsRef.current[selectedIndex];
        if (!fitContent || !selected?.parentElement) return;
        const measure = ()=>setMeasured({
                left: selected.offsetLeft - GAP,
                width: selected.offsetWidth
            });
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(selected.parentElement);
        return ()=>observer.disconnect();
    }, [
        fitContent,
        selectedIndex,
        options
    ]);
    const thumbStyle = fitContent && measured ? {
        width: measured.width,
        transform: `translateX(${measured.left}px)`
    } : {
        width: `calc((100% - ${GAP * (count + 1)}px) / ${count})`,
        transform: `translateX(calc(${selectedIndex} * (100% + ${GAP}px)))`
    };
    const select = (index)=>{
        const next = options[(index + count) % count];
        onChange(next.value);
        buttonsRef.current[(index + count) % count]?.focus();
    };
    const handleKeyDown = (event)=>{
        const moves = {
            ArrowRight: selectedIndex + 1,
            ArrowDown: selectedIndex + 1,
            ArrowLeft: selectedIndex - 1,
            ArrowUp: selectedIndex - 1,
            Home: 0,
            End: count - 1
        };
        if (!(event.key in moves)) return;
        event.preventDefault();
        select(moves[event.key]);
    };
    return /*#__PURE__*/ React.createElement(Track, {
        role: role,
        "aria-label": ariaLabel,
        className: className,
        "data-fit": fitContent,
        onKeyDown: handleKeyDown
    }, /*#__PURE__*/ React.createElement(Thumb, {
        "aria-hidden": true,
        style: thumbStyle
    }), options.map((option, index)=>{
        const isSelected = index === selectedIndex;
        return /*#__PURE__*/ React.createElement(Segment, {
            key: option.value,
            ref: (element)=>{
                buttonsRef.current[index] = element;
            },
            type: "button",
            id: getTabId?.(option.value),
            title: typeof option.label === "string" ? option.label : undefined,
            role: isTabs ? "tab" : "radio",
            "aria-selected": isTabs ? isSelected : undefined,
            "aria-checked": isTabs ? undefined : isSelected,
            "aria-controls": isTabs ? getPanelId?.(option.value) : undefined,
            tabIndex: isSelected ? 0 : -1,
            onClick: ()=>onChange(option.value)
        }, option.label);
    }));
}
